import Editor from "@monaco-editor/react";
import { Close as CloseIcon, Edit as EditIcon } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DictionaryApiService from "../system/integrate/DictionaryApiService";
import Logger from "../../services/Logger";

export default function WordMeaningEditDialog({ open, onClose, word, language = 'english', onSave }) {
    const [meaning, setMeaning] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [editedMeaning, setEditedMeaning] = useState('');

    useEffect(() => {
        if (open && word) {
            fetchWordMeaning();
        } else {
            // Reset state when dialog closes
            setMeaning(null);
            setEditedMeaning('');
            setError(null);
        }
    }, [open, word]);

    const fetchWordMeaning = async () => {
        if (!word) return;

        setLoading(true);
        setError(null);
        try {
            const response = await DictionaryApiService.getWord(word, language);
            
            if (response && response.meaning) {
                // The API returns meaning as an object with partOfSpeech keys
                // Convert it to a string representation for editing
                const meaningStr = JSON.stringify(response.meaning, null, 2);
                // Store the full response to preserve wordNorm and other required fields
                setMeaning(response);
                setEditedMeaning(meaningStr);
            } else {
                setError('No meaning found for this word');
                setMeaning(null);
                setEditedMeaning('');
            }
        } catch (err) {
            Logger.error('Error fetching word meaning:', err);
            setError(err.message || 'Failed to fetch word meaning');
            setMeaning(null);
            setEditedMeaning('');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!word || !editedMeaning.trim()) {
            setError('Please enter a valid meaning');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            // Parse the edited meaning JSON string back to an object
            let meaningObject;
            try {
                meaningObject = JSON.parse(editedMeaning);
            } catch (parseError) {
                setError('Invalid JSON format. Please check your meaning format.');
                setSaving(false);
                return;
            }

            // The backend requires wordNorm to be present in the Dictionary entity
            // Since the API only accepts meaning in the body, we need to ensure
            // the backend can find the existing entity. However, if the backend
            // expects the full object, we should include wordNorm from the original response
            // But based on the API signature, it only accepts meaning as JsonNode
            // So the backend should handle fetching the existing entity and updating only meaning
            
            // Call the PUT API to update the word meaning
            // The backend should fetch the existing Dictionary entity first and update only the meaning
            // The backend is responsible for preserving wordNorm when updating
            const response = await DictionaryApiService.updateWordMeaning(word, meaningObject, language);

            if (response && response.code !== 'FAILURE') {
                // Success - call the onSave callback if provided
                if (onSave) {
                    onSave(response);
                }
                onClose();
            } else {
                setError(response?.message || 'Failed to save word meaning');
            }
        } catch (err) {
            Logger.error('Error saving word meaning:', err);
            setError(err.message || 'Failed to save word meaning');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '400px'
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditIcon color="primary" />
                        <Typography variant="h6" component="span">
                            Edit Word Meaning
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        disabled={saving || loading}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                            '&:hover': {
                                backgroundColor: (theme) => theme.palette.action.hover
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                {word && (
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Word: <strong>{word}</strong> | Language: <strong>{language}</strong>
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <CircularProgress />
                    </Box>
                ) : error && !meaning ? (
                    <Box sx={{ p: 2 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                ) : (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Edit the meaning JSON below. The meaning should be an object with part-of-speech keys (e.g., "noun", "verb", "adjective") and their definitions as values.
                        </Typography>
                        
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        
                        <Box sx={{ 
                            border: error ? '1px solid #d32f2f' : '1px solid #e0e0e0',
                            borderRadius: 1,
                            overflow: 'hidden',
                            height: '400px'
                        }}>
                            <Editor
                                height="400px"
                                defaultLanguage="json"
                                value={editedMeaning}
                                onChange={(value) => {
                                    setEditedMeaning(value || '');
                                    setError(null); // Clear error when user starts typing
                                }}
                                theme="vs-light"
                                options={{
                                    minimap: { enabled: false },
                                    scrollBeyondLastLine: false,
                                    fontSize: 14,
                                    wordWrap: 'on',
                                    formatOnPaste: true,
                                    formatOnType: true,
                                    tabSize: 2,
                                    automaticLayout: true,
                                    tabFocusMode: false  // Tab inserts indent; use Ctrl+M to toggle focus navigation
                                }}
                            />
                        </Box>
                        <Alert 
                            severity="warning" 
                            sx={{ 
                                mt: 2,
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                '& .MuiAlert-icon': {
                                    color: '#ff9800'
                                }
                            }}
                        >
                            <Typography variant="body2">
                                Changes will affect users' pages <strong>immediately</strong> after saving. 
                                The system will verify the JSON format before saving.
                            </Typography>
                        </Alert>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={saving || loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || loading || !editedMeaning.trim()}
                    startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

